import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getImageUrl } from "../../services/api";

const DetailBlogGallery = ({ gallery }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const handleOpen = (index) => {
    setSelectedImageIndex(index);
    document.body.style.overflow = 'hidden'; // Disable scroll
  };

  const handleClose = React.useCallback(() => {
    setSelectedImageIndex(null);
    document.body.style.overflow = 'unset'; // Enable scroll
  }, []);

  const handleNext = React.useCallback((e) => {
    if (e) e.stopPropagation();
    if (gallery) {
      setSelectedImageIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));
    }
  }, [gallery]);

  const handlePrev = React.useCallback((e) => {
    if (e) e.stopPropagation();
    if (gallery) {
      setSelectedImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
    }
  }, [gallery]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowRight") handleNext(e);
      if (e.key === "ArrowLeft") handlePrev(e);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex, handleClose, handleNext, handlePrev]);

  if (!gallery || gallery.length === 0) return null;

  return (
    <div className="w-full">
      <h3 className="mb-8 text-3xl font-bold text-gray-900 dark:text-white">
        Gallery
      </h3>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {gallery.map((image, index) => {
          const imageUrl = getImageUrl(image);

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => handleOpen(index)}
              className="group relative cursor-pointer overflow-hidden rounded-2xl shadow-md transition-shadow hover:shadow-xl"
            >
              <img
                src={
                  imageUrl && !imageUrl.includes("via.placeholder.com") && !imageUrl.includes("C:\\") && !imageUrl.includes("C:/")
                    ? imageUrl
                    : "https://placehold.co/600x400?text=Gallery+Image"
                }
                alt={`Gallery ${index + 1}`}
                className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-110"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://placehold.co/600x400?text=Gallery+Image";
                }}
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform scale-75 group-hover:scale-100">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                </svg>
              </div>
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5 dark:ring-white/10 rounded-2xl pointer-events-none" />
            </motion.div>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 sm:p-8"
            onClick={handleClose}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-50 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors sm:top-8 sm:right-8"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-8 w-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Prev Button */}
            <button
              onClick={handlePrev}
              className="absolute left-4 z-50 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors sm:left-8 backdrop-blur-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6 sm:h-8 sm:w-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={handleNext}
              className="absolute right-4 z-50 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors sm:right-8 backdrop-blur-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6 sm:h-8 sm:w-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>

            {/* Slider Content */}
            <div className="relative h-full w-full max-w-7xl flex items-center justify-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode='wait'>
                <motion.img
                  key={selectedImageIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  src={getImageUrl(gallery[selectedImageIndex])}
                  className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
                  alt={`Gallery preview ${selectedImageIndex + 1}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://placehold.co/1200x800?text=Preview+Image";
                  }}
                />
              </AnimatePresence>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-4 py-1 text-sm font-medium text-white backdrop-blur-md">
                {selectedImageIndex + 1} / {gallery.length}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DetailBlogGallery;
